import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { fortnightStart, addDays, todayMel } from "@/lib/time";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, Send, Lock, Copy, LayoutTemplate, MailCheck } from "lucide-react";

const WEEKDAYS = [
  { v: "0", n: "Monday" }, { v: "1", n: "Tuesday" }, { v: "2", n: "Wednesday" },
  { v: "3", n: "Thursday" }, { v: "4", n: "Friday" }, { v: "5", n: "Saturday" }, { v: "6", n: "Sunday" },
];

export default function AdminRoster() {
  const [staff, setStaff] = useState([]);
  const [userId, setUserId] = useState("");
  const [fs, setFs] = useState(fortnightStart(todayMel()));
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null); // {mode:'add'|'edit', shift}
  const [form, setForm] = useState({ date: todayMel(), start: "09:00", end: "17:00" });
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyCount, setCopyCount] = useState("1");
  const [tplOpen, setTplOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplForm, setTplForm] = useState({ name: "", entries: [{ weekday: "0", start: "09:00", end: "17:00" }] });
  const [view, setView] = useState("staff");
  const [coverage, setCoverage] = useState(null);

  useEffect(() => {
    api.get("/admin/staff").then((r) => {
      setStaff(r.data);
      if (r.data.length && !userId) setUserId(r.data[0].id);
    });
  }, []); // eslint-disable-line

  const load = useCallback(async () => {
    if (!userId) { setData(null); return; }
    const { data } = await api.get("/admin/roster", { params: { user_id: userId, fortnight_start: fs } });
    setData(data);
  }, [userId, fs]);
  useEffect(() => { load(); }, [load]);

  const loadCoverage = useCallback(async () => {
    const { data } = await api.get("/admin/roster/coverage", { params: { fortnight_start: fs } });
    setCoverage(data);
  }, [fs]);
  useEffect(() => { if (view === "coverage") loadCoverage(); }, [view, loadCoverage]);

  const openAdd = () => { setForm({ date: fs, start: "09:00", end: "17:00" }); setDialog({ mode: "add" }); };
  const openEdit = (s) => { setForm({ date: s.date, start: s.start, end: s.end }); setDialog({ mode: "edit", shift: s }); };

  const save = async () => {
    try {
      if (dialog.mode === "add") {
        await api.post("/admin/roster", { user_id: userId, ...form });
        toast.success("Roster shift added (remember to publish).");
      } else {
        await api.patch(`/admin/roster/${dialog.shift.id}`, form);
        toast.success("Roster shift updated (re-publish to share).");
      }
      setDialog(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (s) => {
    try {
      await api.delete(`/admin/roster/${s.id}`);
      toast.success("Roster shift removed.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const publish = async () => {
    try {
      const { data } = await api.post("/admin/roster/publish", { user_id: userId, fortnight_start: fs });
      toast.success(data.message);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const unpublish = async () => {
    try {
      const { data } = await api.post("/admin/roster/unpublish", { user_id: userId, fortnight_start: fs });
      toast.success(data.message);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const copyPrevious = async () => {
    try {
      const { data } = await api.post("/admin/roster/copy", { user_id: userId, fortnight_start: fs, count: parseInt(copyCount, 10) });
      toast.success(data.message);
      setCopyOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const publishAll = async () => {
    try {
      const { data } = await api.post("/admin/roster/publish-all", { fortnight_start: fs });
      toast.success(data.message);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const loadTemplates = async () => {
    const { data } = await api.get("/admin/roster/templates");
    setTemplates(data);
  };
  const openTemplates = async () => { await loadTemplates(); setTplOpen(true); };

  const saveTemplate = async () => {
    try {
      await api.post("/admin/roster/templates", {
        name: tplForm.name,
        entries: tplForm.entries.map((e) => ({ weekday: parseInt(e.weekday, 10), start: e.start, end: e.end })),
      });
      toast.success("Template saved.");
      setTplForm({ name: "", entries: [{ weekday: "0", start: "09:00", end: "17:00" }] });
      loadTemplates();
    } catch (err) { toast.error(apiError(err)); }
  };
  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/admin/roster/templates/${id}`);
      toast.success("Template removed.");
      loadTemplates();
    } catch (err) { toast.error(apiError(err)); }
  };
  const applyTemplate = async (id) => {
    if (!userId) { toast.error("Select a staff member first."); return; }
    try {
      const { data } = await api.post("/admin/roster/apply-template", { user_id: userId, fortnight_start: fs, template_id: id });
      toast.success(data.message);
      setTplOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-roster">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-bold">Roster</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={openTemplates} data-testid="templates-btn"><LayoutTemplate className="h-4 w-4 mr-2" /> Templates</Button>
          <Button variant="outline" onClick={publishAll} data-testid="publish-all-btn"><MailCheck className="h-4 w-4 mr-2" /> Publish All</Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => setFs(addDays(fs, -14))} data-testid="admin-roster-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm font-medium px-2" data-testid="admin-roster-range">{data?.fortnight_start_display} – {data?.fortnight_end_display}</span>
            <Button variant="outline" size="icon" onClick={() => setFs(addDays(fs, 14))} data-testid="admin-roster-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList data-testid="roster-view-tabs">
          <TabsTrigger value="staff" data-testid="view-staff">By Staff</TabsTrigger>
          <TabsTrigger value="coverage" data-testid="view-coverage">Coverage</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "staff" && (<>
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-2 min-w-[220px]">
          <Label>Staff member</Label>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger data-testid="roster-staff-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
            <SelectContent>
              {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openAdd} variant="outline" disabled={!userId} data-testid="add-roster-btn"><Plus className="h-4 w-4 mr-2" /> Add Shift</Button>
        <Button onClick={() => { setCopyCount("1"); setCopyOpen(true); }} variant="outline" disabled={!userId} data-testid="copy-roster-btn"><Copy className="h-4 w-4 mr-2" /> Copy Previous Fortnight</Button>
        {data && (data.published ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-2 text-sm text-[hsl(150,45%,28%)] bg-[hsl(150,40%,92%)] px-3 py-2 rounded-lg" data-testid="roster-published-banner">
              <Lock className="h-4 w-4" /> Published{data.publish_info?.published_by ? ` by ${data.publish_info.published_by}` : ""}{data.publish_info?.published_at_display ? ` on ${data.publish_info.published_at_display}` : ""}
            </span>
            <Button onClick={unpublish} variant="outline" data-testid="unpublish-roster-btn">Unpublish</Button>
          </div>
        ) : (
          <Button onClick={publish} disabled={!data.shifts.length} data-testid="publish-roster-btn"><Send className="h-4 w-4 mr-2" /> Publish to Staff</Button>
        ))}
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Card className="p-4 rounded-xl border-border">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Total rostered</p>
              <p className="font-heading text-2xl font-bold mt-1" data-testid="total-rostered">{data.total_rostered_display}</p>
            </Card>
            <Card className="p-4 rounded-xl border-border">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Actual worked</p>
              <p className="font-heading text-2xl font-bold mt-1" data-testid="total-actual">{data.total_actual_display}</p>
            </Card>
          </div>

          <Card className="rounded-xl border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Day</TableHead>
                  <TableHead>Rostered</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Actual worked</TableHead>
                  <TableHead className="text-right">Edit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="roster-rows">
                {data.shifts.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No roster shifts this fortnight. Add one above.</TableCell></TableRow>}
                {data.shifts.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.date_display}</TableCell>
                    <TableCell>{s.weekday}</TableCell>
                    <TableCell>{s.start_display} – {s.end_display}</TableCell>
                    <TableCell className="font-semibold">{s.rostered_display}</TableCell>
                    <TableCell className={s.actual_minutes && Math.abs(s.actual_minutes - s.rostered_minutes) > 15 ? "text-[hsl(33,65%,40%)] font-medium" : ""}>{s.actual_display}</TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-roster-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(s)} data-testid={`delete-roster-${s.id}`}><Trash2 className="h-4 w-4 text-[hsl(8,54%,42%)]" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
      </>)}

      {view === "coverage" && coverage && (
        <Card className="rounded-xl border-border overflow-x-auto" data-testid="coverage-grid">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 bg-secondary text-left p-3 font-heading font-semibold border-b border-border min-w-[140px]">Staff</th>
                {coverage.days.map((d) => (
                  <th key={d.date} className="p-2 text-center border-b border-l border-border min-w-[90px]">
                    <div className="font-medium">{d.weekday}</div>
                    <div className="text-xs text-muted-foreground">{d.date_display}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {coverage.rows.length === 0 && (
                <tr><td colSpan={coverage.days.length + 1} className="text-center text-muted-foreground py-8">No active staff.</td></tr>
              )}
              {coverage.rows.map((row) => (
                <tr key={row.user_id} className="align-top">
                  <td className="sticky left-0 bg-card font-medium p-3 border-b border-border">{row.staff_name}</td>
                  {row.cells.map((cell) => (
                    <td key={cell.date} className="p-1.5 border-b border-l border-border text-center">
                      {cell.shifts.length === 0 ? (
                        <span className="text-muted-foreground/40">·</span>
                      ) : (
                        <div className="space-y-1">
                          {cell.shifts.map((sh, i) => (
                            <div key={i} className={`rounded px-1.5 py-1 text-xs leading-tight ${sh.clash ? "bg-[hsl(8,60%,92%)] text-[hsl(8,54%,38%)] font-medium" : sh.published ? "bg-[hsl(150,40%,90%)] text-[hsl(150,45%,26%)]" : "bg-secondary text-secondary-foreground"}`} title={sh.clash ? "Overlapping shift" : sh.published ? "Published" : "Draft"}>
                              {sh.start_display}–{sh.end_display}{sh.clash ? " ⚠" : ""}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="bg-secondary/60">
                <td className="sticky left-0 bg-secondary/60 p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground border-t border-border">Staff rostered</td>
                {coverage.days.map((d) => (
                  <td key={d.date} className={`p-2 text-center border-t border-l border-border font-semibold ${d.staff_count === 0 ? "text-[hsl(8,54%,42%)]" : ""}`} data-testid={`coverage-count-${d.date}`}>
                    {d.staff_count === 0 ? "gap" : d.staff_count}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="templates-dialog">
          <DialogHeader><DialogTitle>Roster Templates</DialogTitle></DialogHeader>

          <div className="space-y-4">
            <div>
              <h3 className="font-heading font-semibold text-sm mb-2">Saved templates</h3>
              {templates.length === 0 && <p className="text-sm text-muted-foreground">No templates yet. Create a weekly pattern below.</p>}
              <div className="space-y-2" data-testid="templates-list">
                {templates.map((t) => (
                  <Card key={t.id} className="p-3 rounded-lg border-border">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium">{t.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t.entries.map((e) => `${e.weekday_name.slice(0, 3)} ${e.start_display}–${e.end_display}`).join(" · ")}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" onClick={() => applyTemplate(t.id)} disabled={!userId} data-testid={`apply-template-${t.id}`}>Apply</Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteTemplate(t.id)} data-testid={`delete-template-${t.id}`}><Trash2 className="h-4 w-4 text-[hsl(8,54%,42%)]" /></Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              {!userId && templates.length > 0 && <p className="text-xs text-[hsl(8,54%,42%)] mt-2">Select a staff member on the roster page to apply a template.</p>}
            </div>

            <div className="border-t border-border pt-4">
              <h3 className="font-heading font-semibold text-sm mb-2">Create a new template</h3>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Template name</Label>
                  <Input value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} placeholder="e.g. Reception — Weekdays 9-5" data-testid="template-name" />
                </div>
                {tplForm.entries.map((en, i) => (
                  <div key={i} className="flex items-end gap-2" data-testid={`template-entry-${i}`}>
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs">Day</Label>
                      <Select value={en.weekday} onValueChange={(v) => {
                        const next = [...tplForm.entries]; next[i] = { ...next[i], weekday: v }; setTplForm({ ...tplForm, entries: next });
                      }}>
                        <SelectTrigger data-testid={`template-weekday-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{WEEKDAYS.map((d) => <SelectItem key={d.v} value={d.v}>{d.n}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Start</Label>
                      <Input type="time" value={en.start} onChange={(e) => { const next = [...tplForm.entries]; next[i] = { ...next[i], start: e.target.value }; setTplForm({ ...tplForm, entries: next }); }} data-testid={`template-start-${i}`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">End</Label>
                      <Input type="time" value={en.end} onChange={(e) => { const next = [...tplForm.entries]; next[i] = { ...next[i], end: e.target.value }; setTplForm({ ...tplForm, entries: next }); }} data-testid={`template-end-${i}`} />
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => setTplForm({ ...tplForm, entries: tplForm.entries.filter((_, j) => j !== i) })} disabled={tplForm.entries.length === 1} data-testid={`template-remove-${i}`}><Trash2 className="h-4 w-4 text-[hsl(8,54%,42%)]" /></Button>
                  </div>
                ))}
                <Button variant="ghost" size="sm" onClick={() => setTplForm({ ...tplForm, entries: [...tplForm.entries, { weekday: "0", start: "09:00", end: "17:00" }] })} data-testid="template-add-row">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add day
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={saveTemplate} disabled={!tplForm.name || tplForm.entries.length === 0} data-testid="template-save">Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent data-testid="copy-roster-dialog">
          <DialogHeader><DialogTitle>Copy Previous Fortnight Forward</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Copies the fortnight ending just before <span className="font-medium text-foreground">{data?.fortnight_start_display}</span> into this fortnight and onward, as unpublished drafts.
            </p>
            <div className="space-y-2">
              <Label>How many fortnights forward?</Label>
              <Select value={copyCount} onValueChange={setCopyCount}>
                <SelectTrigger data-testid="copy-count-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 6, 8, 12].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n} fortnight{n > 1 ? "s" : ""} ({n * 2} weeks)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={copyPrevious} data-testid="copy-confirm">Copy Roster</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="roster-dialog">
          <DialogHeader><DialogTitle>{dialog?.mode === "add" ? "Add Roster Shift" : "Edit Roster Shift"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="roster-date" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Start time</Label>
                <Input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="roster-start" /></div>
              <div className="space-y-2"><Label>End time</Label>
                <Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="roster-end" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={!form.date || !form.start || !form.end} data-testid="roster-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
